import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { apiClient } from '@/lib/api-client';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';
import { isStale, problemMessage, standingChanged } from '../api/problem';
import { scheduleUndoable } from '../ui/undoable';
import type { components } from '@/lib/api-types-administration';

type Person = { readonly id: string; readonly displayName: string };
type Invitation = components['schemas']['Invitation'];

/** A failed write, said in plain words: thrown inside a withheld write, caught by its caller. */
class WriteRefused extends Error {
  constructor(
    readonly body: unknown,
    readonly status: number,
  ) {
    super(`refused with ${status}`);
  }
}

type Run = ReturnType<typeof useKeyedPendingWrite>['run'];
type Shared = {
  readonly run: Run;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly onChanged: () => void;
  readonly say: (error: unknown) => void;
  readonly platformHeader: () => {
    'X-Active-Tenant-Id': string;
    'X-Active-Organization-Id': string;
  };
};

/** Remove a person from the place. */
function usePersonActs({ run, placeKind, placeId, placeName, onChanged, say }: Shared) {
  const { t } = useTranslation('administration');
  const remove = useCallback(
    (person: Person, hooks: { hide?: () => void; restore?: () => void } = {}) =>
      run(`remove:${person.id}`, async () => {
        hooks.hide?.();
        try {
          const outcome = await scheduleUndoable({
            id: `remove-${person.id}`,
            message: t('people.toast.removed', { name: person.displayName, place: placeName }),
            undoLabel: t('common.undo'),
            countdown: (seconds) => t('common.countdown', { seconds }),
            run: async () => {
              const { error, response } = await apiClient.POST(
                '/v1/administration/{placeKind}/{placeId}/users/{userId}/removal',
                { params: { path: { placeKind, placeId, userId: person.id } }, body: {} },
              );
              if (!response.ok) {
                throw new WriteRefused(error, response.status);
              }
            },
          });
          if (outcome === 'undone') {
            hooks.restore?.();
            return;
          }
          onChanged();
        } catch (error) {
          hooks.restore?.();
          say(error);
        }
      }),
    [run, t, placeName, placeKind, placeId, onChanged, say],
  );

  return { remove };
}

/** Deactivate and reactivate a person: the platform's routes, in the caller's active context. */
function usePlatformActs({ run, onChanged, say, platformHeader }: Shared) {
  const { t } = useTranslation('administration');
  const deactivate = useCallback(
    (person: Person) =>
      run(`deactivate:${person.id}`, async () => {
        try {
          const outcome = await scheduleUndoable({
            id: `deactivate-${person.id}`,
            message: t('people.toast.deactivated', { name: person.displayName }),
            undoLabel: t('common.undo'),
            countdown: (seconds) => t('common.countdown', { seconds }),
            run: async () => {
              const { error, response } = await apiClient.POST(
                '/v1/administration/platform/users/{userId}/deactivation',
                { params: { path: { userId: person.id }, header: platformHeader() }, body: {} },
              );
              if (!response.ok) {
                throw new WriteRefused(error, response.status);
              }
            },
          });
          if (outcome === 'done') {
            onChanged();
          }
        } catch (error) {
          say(error);
        }
      }),
    [run, t, onChanged, say, platformHeader],
  );

  const reactivate = useCallback(
    (person: Person) =>
      run(`reactivate:${person.id}`, async () => {
        try {
          const { error, response } = await apiClient.POST(
            '/v1/administration/platform/users/{userId}/reactivation',
            { params: { path: { userId: person.id }, header: platformHeader() } },
          );
          if (!response.ok) {
            throw new WriteRefused(error, response.status);
          }
          toast.success(t('people.toast.reactivated', { name: person.displayName }));
          onChanged();
        } catch (error) {
          say(error);
        }
      }),
    [run, t, onChanged, say, platformHeader],
  );

  return { deactivate, reactivate };
}

/** Withdraw and renew an invitation. */
function useInvitationActs({ run, placeKind, placeId, onChanged, say }: Shared) {
  const { t } = useTranslation('administration');
  const withdraw = useCallback(
    (invitation: Invitation) =>
      run(`withdraw:${invitation.id}`, async () => {
        try {
          const { error, response } = await apiClient.POST(
            '/v1/administration/{placeKind}/{placeId}/invitations/{invitationId}/withdrawal',
            { params: { path: { placeKind, placeId, invitationId: invitation.id } } },
          );
          if (!response.ok) {
            throw new WriteRefused(error, response.status);
          }
          toast.success(t('people.toast.withdrawn', { email: invitation.email }));
          onChanged();
        } catch (error) {
          say(error);
        }
      }),
    [run, t, placeKind, placeId, onChanged, say],
  );

  const renew = useCallback(
    (invitation: Invitation) =>
      run(`renew:${invitation.id}`, async () => {
        try {
          const { error, response } = await apiClient.POST(
            '/v1/administration/{placeKind}/{placeId}/invitations/{invitationId}/renewal',
            { params: { path: { placeKind, placeId, invitationId: invitation.id } } },
          );
          if (!response.ok) {
            throw new WriteRefused(error, response.status);
          }
          toast.success(t('people.toast.renewed', { email: invitation.email }));
          onChanged();
        } catch (error) {
          say(error);
        }
      }),
    [run, t, placeKind, placeId, onChanged, say],
  );

  return { withdraw, renew };
}

/**
 * The acts on a person and on an invitation, shared by Utenti's rows and the person's sheet so
 * both say and do exactly the same thing.
 *
 * - «Rimuovi da qui» and «Disattiva» take the full undo model (FR-UX-5): the change shows at once,
 *   the write is withheld for 6 seconds, «Annulla» cancels it. No dialog comes first.
 * - «Riattiva», «Ritira» and «Invita di nuovo» are each reversed by an act that exists, so they run
 *   at once and a toast confirms them.
 *
 * Every write is guarded while in flight (CST-AD-2), keyed by its object, so a second press on the
 * same row does nothing rather than sending twice.
 */
export function useAccessActions({
  placeKind,
  placeId,
  placeName,
  onChanged,
  onStanding,
}: {
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
  readonly onChanged: () => void;
  /** The caller's standing changed after the reads: the place must be read again. */
  readonly onStanding?: () => void;
}) {
  const { t } = useTranslation('administration');
  const { isPending, run } = useKeyedPendingWrite();
  // The platform's routes are evaluated in the caller's own active organization, and carry it.
  const active = useActiveContextOptional()?.active ?? null;
  const platformHeader = useCallback(() => {
    if (active === null) {
      throw new WriteRefused({ code: 'ACTIVE_CONTEXT_REQUIRED' }, 400);
    }
    return {
      'X-Active-Tenant-Id': active.tenantId,
      'X-Active-Organization-Id': active.organizationId,
    };
  }, [active]);

  const say = useCallback(
    (error: unknown) => {
      if (error instanceof WriteRefused) {
        toast.error(problemMessage(t, error.body, error.status));
        // What the screen showed is no longer true: read again rather than offer a dead button.
        if (standingChanged(error.body)) {
          onStanding?.();
        }
        if (isStale(error.body)) {
          onChanged();
        }
      } else {
        toast.error(t('problems.unavailable'));
      }
    },
    [t, onChanged, onStanding],
  );

  const shared = { run, placeKind, placeId, placeName, onChanged, say, platformHeader };
  const person = usePersonActs(shared);
  const platform = usePlatformActs(shared);
  const invitation = useInvitationActs(shared);

  return { ...person, ...platform, ...invitation, isPending };
}
